import { useCallback, useMemo, useState } from 'react';
import type {
    CompanyMappings,
    FolderData,
    Tab,
    TabContent,
    TimelineEntry,
} from '../types/folder-label';

const INITIAL_FOLDERS: FolderData[] = [
    {
        id: 1,
        name: 'Financial',
        children: [
            { id: 11, name: 'Tax Documents', children: [] },
            { id: 12, name: 'Invoices', children: [] },
            { id: 13, name: 'Expenses', children: [] },
        ],
    },
    {
        id: 2,
        name: 'Legal',
        children: [
            { id: 21, name: 'Contracts', children: [] },
            { id: 22, name: 'Compliance', children: [] },
        ],
    },
    {
        id: 3,
        name: 'Investor Relations',
        children: [
            { id: 31, name: 'Updates', children: [] },
            { id: 32, name: 'Reports', children: [] },
        ],
    },
    {
        id: 4,
        name: 'Operations',
        children: [],
    },
];

const INITIAL_COMPANY_MAPPINGS: CompanyMappings = {
    Fruitful: {
        31: 'investor_updates',
        13: 'quarterly_expenses',
    },
    Barachou: {
        13: 'UWS_expenses',
        21: 'lease_agreements',
    },
};

const FRUITFUL_DATA: TimelineEntry[] = [
    {
        date: 'JAN 15, 2026',
        relativeTime: '5 days ago',
        count: 1,
        type: 'email',
        items: [
            {
                title: 'Fwd: Fruitful Investor Update: Sep 2025',
                folder: 'Investor Relations > Updates',
                from: 'Elliot Benaudis <elliot@notissia.com>',
                preview:
                    'Forwarded message - From: Lee Linden <lee@quiet.com> Date: Fri, Aug 15, 2025...',
            },
        ],
    },
    {
        date: 'JAN 9, 2026',
        relativeTime: '11 days ago',
        count: 1,
        type: 'email',
        items: [
            {
                title: 'Fwd: Fruitful Investor Update: Sep 2025',
                folder: 'Investor Relations > Updates',
                from: 'Elliot Benaudis <elliot@notissia.com>',
                preview:
                    'Forwarded message - From: Elliot Benaudis <elliot@notissia.com> Date: Tue, Dec...',
            },
        ],
    },
];

const BARACHOU_DATA: TimelineEntry[] = [
    {
        date: 'JAN 20, 2026',
        relativeTime: '2 hours ago',
        count: 1,
        type: 'file',
        items: [
            {
                title: 'Barachou - External Link',
                folder: 'Financial > Expenses',
                isLink: true,
                link: 'https://docs.google.com/spreadshe...',
            },
        ],
    },
    {
        date: 'JAN 9, 2026',
        relativeTime: '11 days ago',
        count: 1,
        type: 'file',
        items: [
            {
                title: 'Barachou (UWS) Expenses 2023.xls',
                folder: 'Financial > Expenses',
            },
        ],
    },
];

const TABS: Tab[] = [
    { id: 'fruitful', label: 'Fruitful' },
    { id: 'barachou', label: 'Barachou' },
];

interface UseFolderLabelOptions {
    initialFolders?: FolderData[];
    initialCompanyMappings?: CompanyMappings;
    initialTab?: string;
}

interface UseFolderLabelReturn {
    // Tab state
    tabs: Tab[];
    activeTab: string;
    setActiveTab: (tab: string) => void;
    selectedCompany: string;
    tabContent: TabContent;

    // Modal state
    showFolderModal: boolean;
    openFolderModal: () => void;
    closeFolderModal: () => void;

    // Folder data
    folders: FolderData[];
    companyMappings: CompanyMappings;

    // Folder operations
    addFolder: (name: string) => void;
    deleteFolder: (id: number) => void;
    addSubfolder: (parentId: number, name: string) => void;
    renameFolder: (id: number, newName: string) => void;
    moveFolder: (folderId: number, newParentId: number | null) => void;

    // Company mapping operations
    setCompanyMapping: (company: string, folderId: number, mapping: string) => void;
    removeCompanyMapping: (company: string, folderId: number) => void;
}

export const useFolderLabel = (options: UseFolderLabelOptions = {}): UseFolderLabelReturn => {
    const {
        initialFolders = INITIAL_FOLDERS,
        initialCompanyMappings = INITIAL_COMPANY_MAPPINGS,
        initialTab = 'fruitful',
    } = options;

    // State
    const [activeTab, setActiveTab] = useState<string>(initialTab);
    const [showFolderModal, setShowFolderModal] = useState(false);
    const [folders, setFolders] = useState<FolderData[]>(initialFolders);
    const [companyMappings, setCompanyMappings] = useState<CompanyMappings>(initialCompanyMappings);

    // Derived state
    const selectedCompany = useMemo(
        () => (activeTab === 'fruitful' ? 'Fruitful' : 'Barachou'),
        [activeTab]
    );

    const tabContent = useMemo((): TabContent => {
        if (activeTab === 'fruitful') {
            return {
                sectionTitle: 'EMAIL TIMELINE',
                sectionCount: '7 emails',
                entries: FRUITFUL_DATA,
            };
        }
        return {
            sectionTitle: 'FILE TIMELINE',
            sectionCount: '14 files',
            entries: BARACHOU_DATA,
        };
    }, [activeTab]);

    // Modal handlers
    const openFolderModal = useCallback(() => setShowFolderModal(true), []);
    const closeFolderModal = useCallback(() => setShowFolderModal(false), []);

    // Folder operations
    const addFolder = useCallback((name: string) => {
        const newFolder: FolderData = {
            id: Date.now(),
            name,
            children: [],
        };
        setFolders((prev) => [...prev, newFolder]);
    }, []);

    const deleteFolder = useCallback((id: number) => {
        const removeFolder = (folderList: FolderData[]): FolderData[] => {
            return folderList
                .filter((folder) => folder.id !== id)
                .map((folder) => ({
                    ...folder,
                    children: removeFolder(folder.children),
                }));
        };
        setFolders((prev) => removeFolder(prev));
    }, []);

    const addSubfolder = useCallback((parentId: number, subfolderName: string) => {
        const newFolder: FolderData = {
            id: Date.now(),
            name: subfolderName,
            children: [],
        };

        const addToParent = (folderList: FolderData[]): FolderData[] => {
            return folderList.map((folder) => {
                if (folder.id === parentId) {
                    return { ...folder, children: [...folder.children, newFolder] };
                }
                if (folder.children.length > 0) {
                    return { ...folder, children: addToParent(folder.children) };
                }
                return folder;
            });
        };
        setFolders((prev) => addToParent(prev));
    }, []);

    const renameFolder = useCallback((id: number, newName: string) => {
        const rename = (folderList: FolderData[]): FolderData[] => {
            return folderList.map((folder) => {
                if (folder.id === id) {
                    return { ...folder, name: newName };
                }
                if (folder.children.length > 0) {
                    return { ...folder, children: rename(folder.children) };
                }
                return folder;
            });
        };
        setFolders((prev) => rename(prev));
    }, []);

    const moveFolder = useCallback((folderId: number, newParentId: number | null) => {
        setFolders((prev) => {
            let folderToMove: FolderData | null = null;

            // Find and remove the folder
            const findAndRemove = (folderList: FolderData[]): FolderData[] => {
                return folderList
                    .filter((folder) => {
                        if (folder.id === folderId) {
                            folderToMove = folder;
                            return false;
                        }
                        return true;
                    })
                    .map((folder) => ({
                        ...folder,
                        children: findAndRemove(folder.children),
                    }));
            };

            const withoutFolder = findAndRemove(prev);

            if (!folderToMove) return prev;

            // Add to new parent or root
            if (newParentId === null) {
                return [...withoutFolder, folderToMove];
            }

            const addToNewParent = (folderList: FolderData[]): FolderData[] => {
                return folderList.map((folder) => {
                    if (folder.id === newParentId) {
                        return { ...folder, children: [...folder.children, folderToMove!] };
                    }
                    if (folder.children.length > 0) {
                        return { ...folder, children: addToNewParent(folder.children) };
                    }
                    return folder;
                });
            };

            return addToNewParent(withoutFolder);
        });
    }, []);

    // Company mapping operations
    const setCompanyMapping = useCallback(
        (company: string, folderId: number, mapping: string) => {
            setCompanyMappings((prev) => ({
                ...prev,
                [company]: {
                    ...prev[company],
                    [folderId]: mapping,
                },
            }));
        },
        []
    );

    const removeCompanyMapping = useCallback((company: string, folderId: number) => {
        setCompanyMappings((prev) => {
            const companyMappings = { ...prev[company] };
            delete companyMappings[folderId];
            return {
                ...prev,
                [company]: companyMappings,
            };
        });
    }, []);

    return {
        // Tab state
        tabs: TABS,
        activeTab,
        setActiveTab,
        selectedCompany,
        tabContent,

        // Modal state
        showFolderModal,
        openFolderModal,
        closeFolderModal,

        // Folder data
        folders,
        companyMappings,

        // Folder operations
        addFolder,
        deleteFolder,
        addSubfolder,
        renameFolder,
        moveFolder,

        // Company mapping operations
        setCompanyMapping,
        removeCompanyMapping,
    };
};
